import { MINIAPP_PACKAGE_ID } from '@/constants';
import { getContext } from '@/miniappContext';
import { getAppStore } from '@/store';

// --- Action constants ---

export const ScreenActionType = {
  GENERATE: 'generate',
  GENERATE_CLICK: 'generate_click',
  DOWNLOAD: 'download',
  DOWNLOAD_CLICK: 'download_click',
  UPLOAD: 'upload',
  CLOSE: 'close',
} as const;

export type ScreenActionType = typeof ScreenActionType[keyof typeof ScreenActionType];

export const ItemLoadResult = {
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const;

export type ItemLoadResult = typeof ItemLoadResult[keyof typeof ItemLoadResult];

export const ExportDestination = {
  DOWNLOAD: 'download',
  COPY_LINK: 'copy_link',
} as const;

export type ExportDestination = typeof ExportDestination[keyof typeof ExportDestination];

// --- Settings interface ---

interface ISettings {
  gender?: string;
  style_snap?: string;
  pose_preset?: string;
  hair?: string;
  hair_color?: string;
  background?: string;
  complete_outfits?: string;
  outerwear?: string;
  tops?: string;
  bottoms?: string;
  shoes?: string;
  glasses?: string;
  bags?: string;
  hats?: string;
}

// --- Event param interfaces ---

interface IScreenAction {
  action: ScreenActionType;
  settings?: ISettings;
}

interface IItemLoad {
  result: ItemLoadResult;
  processing_time: number;
  settings?: ISettings & {
    error_type?: string;
    error_message?: string;
  };
}

interface IObjectExport {
  destination: ExportDestination;
}

export function buildSettings(): ISettings {
  const { character, wardrobe } = getAppStore();
  const items = wardrobe.selectedItems;
  const byCategory = (cat: string) => items.find(i => i.category === cat)?.name;
  return {
    gender: character.gender,
    style_snap: character.selectedStyle?.name,
    pose_preset: character.selectedPose?.name,
    hair: character.selectedHairstyle?.name,
    hair_color: character.selectedHairColor,
    background: character.selectedBackground?.name,
    complete_outfits: byCategory('complete-outfits'),
    outerwear: byCategory('outerwear'),
    tops: byCategory('tops'),
    bottoms: byCategory('bottoms'),
    shoes: byCategory('shoes'),
    glasses: byCategory('glasses'),
    bags: byCategory('bags'),
    hats: byCategory('hats'),
  };
}

class Analytics {
  private get context() {
    return getContext();
  }

  screenAction({ action, settings }: IScreenAction) {
    this.context.handlers.sendAnalytics({
      eventName: 'ai_gen_screen_action',
      params: {
        action,
        ...(settings && { settings }),
      },
    });
  }

  itemLoad({ result, processing_time, settings }: IItemLoad) {
    this.context.handlers.sendAnalytics({
      eventName: 'ai_gen_item_load',
      params: {
        result,
        processing_time,
        ...(settings && { settings }),
      },
    });
  }

  objectExport({ destination }: IObjectExport) {
    this.context.handlers.sendAnalytics({
      eventName: 'object_export',
      params: {
        source: MINIAPP_PACKAGE_ID,
        destination,
      },
    });
  }

  objectApply() {
    this.context.handlers.sendAnalytics({
      eventName: 'object_apply',
      params: {
        usedtools: [MINIAPP_PACKAGE_ID],
      },
    });
  }

  contentTry() {
    this.context.handlers.sendAnalytics({
      eventName: 'content_try',
      params: {
        source: MINIAPP_PACKAGE_ID,
      },
    });
  }
}

export default new Analytics();
